/**
 * i18n — app-side boot + locale switching over the kit's @v0y4ge/i18n-runtime.
 *
 * Catalog source: the bundled platform base catalogs from @v0y4ge/i18n-catalog
 * (the same base the server-side getTranslationCatalog merge starts from) —
 * 130 keys across all 7 locales as of kit v4.2 (0ee90d61, REQ-043b). The
 * REQ-043a client fns exist now; the bundled base stays the render source
 * (no tenant overrides exist to merge design-time), while the profile write
 * goes through the real op below.
 *
 * Locale preference: applied session-wide immediately (localStorage +
 * configureI18n), AND persisted to the signed-in user's profile via the
 * typed `setUserLocale` client fn (kit v4.2 — REQ-043a; the mock is
 * session-gated and whoami echoes the write). Callers use
 * `persistLocalePreference` so the render swap stays synchronous.
 *
 * Boot runs at module scope deliberately: it touches NO network (bundled JSON
 * only), so the 2026-07-19 §2 boot-hygiene rule (no fetch before session) holds.
 */
import { configureI18n } from "@v0y4ge/i18n-runtime";
import { listAvailableLocales, loadCatalog, loadDefaultCatalog } from "@v0y4ge/i18n-catalog";
import { setUserLocale, getTranslationCatalog } from "@v0y4ge/api-client";
import { configureDateTime } from "./datetime";

export { t, useT, useLocaleVersion } from "@v0y4ge/i18n-runtime";
/* formatICU is re-exported for the one shape `t` cannot express on its own: an
 * interpolated message that ALSO needs a call-site English fallback. t(key,
 * fallback) and t(key, params) are separate overloads — there is no
 * t(key, fallback, params) — and the fallback branch returns the string verbatim,
 * unformatted. So `formatICU(t(key, fallback), params)` is the only way to get a
 * placeholder and a byte-exact fallback in the same call. Prefer plain
 * t(key, params) once the catalogue carries the key. */
export { formatICU } from "@v0y4ge/i18n-runtime";

const STORE_KEY = "esm.i18n.locale.v1";
let _current = "en";

export function currentLocale(): string { return _current; }
export function availableLocales(): string[] { return listAvailableLocales(); }

/** Display names Intl gets wrong for our shipped locales. `de-CH` renders as
 *  "Schweizer Hochdeutsch" — a label no Swiss user recognises; the spoken
 *  variety is Schweizerdeutsch (operator ruling 2026-07-28). */
const LOCALE_NAME_OVERRIDES: Record<string, string> = {
  "de-CH": "Schweizerdeutsch",
};

/** CD-owned copy overrides, layered on top of the kit catalog wherever it is
 *  configured. `packages/i18n-catalog` is KIT-OWNED and byte-locked — a drop
 *  that diverges under `packages/**` is rejected — so a wording fix to a string
 *  the catalog already owns cannot be made at source here. This is the seam for
 *  those, and the sibling of LOCALE_NAME_OVERRIDES above.
 *
 *  Keyed BY LOCALE on purpose: an English casing fix must never overwrite
 *  another language's translation (title case is an English convention; German
 *  and French do not share it). An entry under `en` also applies to `en-GB` etc.
 *
 *  Every entry should have a REQ asking for the canonical catalog change, and
 *  should be deleted here once the kit ships it:
 *   · queues.home.text10 — "New queue" → "Add Queue" (operator, 2026-08-25; REQ-111). Was
 *     "New Queue" (title-case only, 2026-07-30); the verb changed with the Journey-designer
 *     entry points so Queues reads like Events ("Add Event").
 *   · queues.messageBoards.text7 — "New board" → "Add Board" (operator, 2026-08-25; same
 *     REQ-111 class — folded in rather than given a new number).
 *   · workforce.schedule.addShift — "Add shift" → "Add Shift" (operator, 2026-07-30; same
 *     title-case class as REQ-111, to be folded into it rather than given a new number).
 *     Note this key is also the shift drawer's title and its save button, so all three
 *     read "Add Shift" — one key, one string, deliberately consistent.
 *   · guests.list.…LastUpdated12s.icu — drops a hardcoded "· last updated 12s ago"
 *     tail (2026-08-12, Kathan). The catalog string bakes the literal "12s", so the
 *     guest list claimed a 12-second-old refresh on every render regardless of when
 *     the data actually loaded — a FABRICATED freshness signal, not a stale variable.
 *     Removing the clause is the honest fix; a real relative timestamp needs the hook
 *     to expose a fetch time, which is a kit change. ⚠ `en`-ONLY — the other six
 *     locales still carry the translated "12s ago" tail, so the canonical catalog fix
 *     is still owed across all shipped locales. */
const STRING_OVERRIDES: Record<string, Record<string, string>> = {
  en: {
    "queues.home.text10": "Add Queue",
    "queues.messageBoards.text7": "Add Board",
    "workforce.schedule.addShift": "Add Shift",
    "guests.list.guestsLengthRegisteredCheckedinCheckedInWaitlistOnWaitlistLastUpdated12s.icu":
      "{value0} registered · {value1} checked in · {value2} on waitlist",
  },
};

/** Catalog with any override for `tag` applied — base language first, then the
 *  exact tag, so a region variant can refine what its base language set. */
function withOverrides<T extends object>(tag: string, catalog: T): T {
  const over = { ...(STRING_OVERRIDES[tag.split("-")[0]] || {}), ...(STRING_OVERRIDES[tag] || {}) };
  return Object.keys(over).length ? ({ ...catalog, ...over } as T) : catalog;
}

/** Native-language display name for a locale tag ("de" → "Deutsch"). */
export function localeDisplayName(tag: string): string {
  const override = LOCALE_NAME_OVERRIDES[tag];
  if (override) return override;
  try {
    const name = new Intl.DisplayNames([tag], { type: "language" }).of(tag);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : tag;
  } catch { return tag; }
}

/** BCP-47 primary subtags that render right-to-left. */
const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);
/** True when a locale tag is a right-to-left script (ar/he today; fa/ur ready). */
export function isRtlLocale(tag: string): boolean {
  return RTL_LANGS.has((tag || "").toLowerCase().split(/[-_]/)[0]);
}
/** D2 (2026-07-22 docket): flip the document writing direction to match the
 *  active locale so ar/he render RTL — the app-wide layout-direction handling
 *  the RTL pass hangs off (flex/grid chrome mirrors from `dir`; CSS logical
 *  properties resolve against it). Sets `lang` too for a11y + font shaping. */
export function applyDocumentDirection(tag: string): void {
  if (typeof document === "undefined") return;
  try {
    const el = document.documentElement;
    el.setAttribute("dir", isRtlLocale(tag) ? "rtl" : "ltr");
    el.setAttribute("lang", tag);
  } catch { /* non-DOM/preview env */ }
}

/** Apply a locale app-wide for this session (ui-local — render config only):
 *  resolves the bundled catalog, re-renders every useT() consumer, and
 *  re-formats every date via lib/datetime's reactive config. Profile
 *  persistence is the caller's second step — `persistLocalePreference`. */
export function setAppLocale(tag: string): void {
  const catalog = loadCatalog(tag);
  if (!catalog) return;
  _current = tag;
  try { localStorage.setItem(STORE_KEY, tag); } catch { /* ignore */ }
  configureI18n({ locale: tag, catalog: withOverrides(tag, catalog), fallbackCatalog: withOverrides("en", loadDefaultCatalog()) });
  configureDateTime({ locale: tag });
  applyDocumentDirection(tag);
}

/** Persist the locale to the signed-in user's profile via the typed
 *  `setUserLocale` op (REQ-043a, kit v4.2). Returns "saved", or
 *  "unauthenticated" when the mock's session store has no signed-in persona
 *  (the preview's interim harness sign-in layer doesn't seed it — REQ-040
 *  acceptance retires that seam), or "failed" for real errors — so the
 *  caller can surface an error state only where one is actionable. */
export async function persistLocalePreference(tag: string): Promise<"saved" | "unauthenticated" | "failed"> {
  try {
    await setUserLocale(tag);
    return "saved";
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status === 401) {
      console.warn("setUserLocale: no server-side session (preview harness seam, REQ-040) — locale applied session-wide only.");
      return "unauthenticated";
    }
    console.warn("setUserLocale failed — locale applied for this session only:", e);
    return "failed";
  }
}

/** REQ-045: the bundled catalog above is the synchronous, zero-network boot
 *  path (never blocks first paint on a request). This layers the SERVER-
 *  MERGED catalog on top once it resolves — tenant-wide AND (when a session
 *  has one) org-scoped overrides via `getTranslationCatalog(locale, organizationId)`.
 *  Call after whoami() resolves an `organizationId`, and again on any locale
 *  or org switch; `access.ts` is the caller (avoids a circular import — this
 *  module has no access.ts dependency). A no-op if org+locale are unchanged
 *  from the last successful fetch; failures are silent — the bundled catalog
 *  already applied stays in effect, so this can never regress or block. */
let _lastCatalogKey: string | null = null;
export async function refreshTranslationCatalog(organizationId?: string | null): Promise<void> {
  const key = `${_current}\u0000${organizationId ?? ""}`;
  if (key === _lastCatalogKey) return;
  if (typeof getTranslationCatalog !== "function") return;
  try {
    const res = await getTranslationCatalog(_current, organizationId ?? undefined);
    if (`${_current}\u0000${organizationId ?? ""}` !== key) return; // superseded mid-flight
    _lastCatalogKey = key;
    configureI18n({ locale: _current, catalog: withOverrides(_current, res.entries), fallbackCatalog: withOverrides("en", loadDefaultCatalog()) });
  } catch (e) {
    console.warn("getTranslationCatalog failed — staying on the bundled catalog:", e);
  }
}

(function boot() {
  let saved: string | null = null;
  try { saved = localStorage.getItem(STORE_KEY); } catch { /* ignore */ }
  const tag = saved && listAvailableLocales().includes(saved) ? saved : "en";
  _current = tag;
  configureI18n({ locale: tag, catalog: withOverrides(tag, loadCatalog(tag) || loadDefaultCatalog()), fallbackCatalog: withOverrides("en", loadDefaultCatalog()) });
  if (tag !== "en") configureDateTime({ locale: tag });
  applyDocumentDirection(tag);
})();
